import { type CinImageLabellingTypes } from "@cinnamon/design-system/compounds";

import { type LLMSample, type LLMSampleKVConfigSingleField, type TableFieldItemKVConfig } from "@/types/llmSample";
import { type PipelineVersion, type TargetId } from "@/types/pipeline";

import { type LLM_SAMPLE_EDITOR_MODE } from "./constants";

export enum LLM_SAMPLE_EDITOR_ACTION {
  SET_LLM_SAMPLE = "SET_LLM_SAMPLE",
  SET_LLM_SAMPLE_DISPLAY_NAME_TEMP = "SET_LLM_SAMPLE_DISPLAY_NAME_TEMP",

  SET_PIPELINE_VERSION = "SET_PIPELINE_VERSION",
  SET_IMAGES = "SET_IMAGES",
  SET_IS_LOADING = "SET_IS_LOADING",
  SET_SELECTED_PAGE_INDEX = "SET_SELECTED_PAGE_INDEX",
  SET_SINGLE_FIELDS = "SET_SINGLE_FIELDS",
  SET_TABLE_FIELDS = "SET_TABLE_FIELDS",
  SET_SELECTED_FIELD = "SET_SELECTED_FIELD",
  SET_TABLE_LINECUT = "SET_TABLE_LINECUT",
  SET_SINGLE_LINECUT = "SET_SINGLE_LINECUT",
  SET_EDITOR_MODE = "SET_EDITOR_MODE",
}

export interface LocationState {
  pipelineVersionId: string;
  pipelineId: string;
  sampleId: string;
  displayName?: string;
  fromJobId?: string;
  isNavigateNextJob?: boolean;
}

export interface SingleFieldState extends LLMSampleKVConfigSingleField {
  description: string;
  display_name: string;
}

export interface TableFieldHeaderState extends TableFieldItemKVConfig {
  description: string;
  display_name: string;
}

export type TableFieldRowStateId = string;

export type TableFieldRowState = { id: TableFieldRowStateId } & Record<TargetId, TableFieldItemKVConfig> & {
    pageNumber?: number;
  };

export interface TableFieldState {
  headers: TableFieldHeaderState[];
  rows: TableFieldRowState[];
}

export enum FieldType {
  SINGLE_FIELDS = "singleFields",
  TABLE_FIELDS = "tableFields",
}
export enum SingleType {
  KEY = "key",
  VALUE = "value",
}
export enum TableType {
  HEADERS = "headers",
  ROWS = "rows",
}
export type FieldValueType = SingleType | TableType;

export interface SelectedFieldState {
  fieldType?: FieldType;
  singleType?: SingleType;
  tableType?: TableType;
  rowId?: TableFieldRowStateId;
  targetId?: TargetId;
}

// - Linecut Id will be created by the way of linecut_${id/targetId}"
export type LinecutId = string;
export type LinecutType = CinImageLabellingTypes.RectangleLinecutBoxInfo & {
  targetId?: TargetId;
  // With new linecut, we need to create a temp id to identify the linecut
  tempLinecutId?: string;
} & (
    | {
        fieldValueType?: Exclude<FieldValueType, TableType.ROWS>;
      }
    | {
        fieldValueType: TableType.ROWS;
        rowId: TableFieldRowStateId;
      }
  );
export type ConfigItemLinecut = Record<LinecutId, LinecutType>;

export interface LLMSampleEditorState {
  llmSample: LLMSample | null;

  // Use incase edit mode is Create New
  displayNameTemp: string | null;

  editorMode: LLM_SAMPLE_EDITOR_MODE;
  images: string[];
  thumbnails: string[];
  pipelineVersion: PipelineVersion | null;
  isLoading: boolean;
  selectedPageIndex: number;
  singleFields: SingleFieldState[];
  tableFields: TableFieldState;
  selectedField: SelectedFieldState | null;

  singleLinecut: ConfigItemLinecut | null;
  tableLinecut: ConfigItemLinecut | null;
}

export type LLMSampleEditorAction =
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_LLM_SAMPLE; payload: LLMSample | null }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_PIPELINE_VERSION; payload: PipelineVersion | null }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_IMAGES; payload: { images: string[]; thumbnails: string[] } }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_IS_LOADING; payload: boolean }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_SELECTED_PAGE_INDEX; payload: number }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_SINGLE_FIELDS; payload: SingleFieldState[] }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_TABLE_FIELDS; payload: TableFieldState }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_SELECTED_FIELD; payload: SelectedFieldState | null }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_TABLE_LINECUT; payload: ConfigItemLinecut | null }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_SINGLE_LINECUT; payload: ConfigItemLinecut | null }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_EDITOR_MODE; payload: LLM_SAMPLE_EDITOR_MODE }
  | { type: LLM_SAMPLE_EDITOR_ACTION.SET_LLM_SAMPLE_DISPLAY_NAME_TEMP; payload: string | null };
